import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, Outlet } from 'react-router-dom';
import { LogOut, FileText, Users as UsersIcon, ExternalLink, Menu, X } from 'lucide-react';
import {
  api,
  getAdminToken,
  clearAdminToken,
  getCurrentUser,
  setCurrentUser,
  hasPermission,
  ROLE_LABELS,
} from '../lib/api';

const AdminLayout = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getCurrentUser());
  const [ready, setReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login');
      return;
    }
    api
      .get('/admin/verify')
      .then((res) => {
        if (res.data?.user) {
          setCurrentUser(res.data.user);
          setUser(res.data.user);
        }
        setReady(true);
      })
      .catch(() => {
        clearAdminToken();
        navigate('/admin/login');
      });
  }, [navigate]);

  const handleLogout = () => {
    clearAdminToken();
    setCurrentUser(null);
    setUser(null);
    setSidebarOpen(false);
    navigate('/admin/login', { replace: true });
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading admin...
      </div>
    );
  }

  const navItem = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? 'bg-brand-blue text-white' : 'text-gray-700 hover:bg-gray-100'
    }`;

  const sidebar = (
    <>
      <div className="p-5 border-b border-gray-200">
        <div className="flex items-start justify-between gap-3">
          <Link to="/admin/blogs" className="block" onClick={() => setSidebarOpen(false)}>
            <span className="text-lg font-bold text-brand-blue">ReCircle</span>
            <span className="block text-xs text-gray-500">Knowledge Centre Admin</span>
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 -mr-2 -mt-2 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Close admin menu"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        <NavLink to="/admin/blogs" className={navItem} data-testid="admin-nav-blogs" onClick={() => setSidebarOpen(false)}>
          <FileText size={16} /> Blogs
        </NavLink>
        {hasPermission('user:read') && (
          <NavLink to="/admin/users" className={navItem} data-testid="admin-nav-users" onClick={() => setSidebarOpen(false)}>
            <UsersIcon size={16} /> Users
          </NavLink>
        )}
        <a
          href="/blog"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100"
          onClick={() => setSidebarOpen(false)}
        >
          <ExternalLink size={16} /> View site
        </a>
      </nav>
      <div className="p-3 border-t border-gray-200">
        <div className="px-3 py-2 mb-2 rounded-md bg-gray-50">
          <p className="text-xs text-gray-500">Signed in as</p>
          <p className="text-sm font-medium text-gray-900 truncate">{user?.name || user?.email}</p>
          <p className="text-xs text-brand-blue">{ROLE_LABELS[user?.role] || user?.role}</p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm"
        >
          <LogOut size={14} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-30 w-60 bg-white border-r border-gray-200 flex-col">
        {sidebar}
      </aside>

      <div className="flex h-full min-w-0 flex-col lg:pl-60">
        <header className="lg:hidden z-30 flex shrink-0 items-center justify-between bg-white border-b border-gray-200 px-4 py-3">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-md text-gray-700 hover:bg-gray-100"
            aria-label="Open admin menu"
            data-testid="admin-menu-toggle"
          >
            <Menu size={22} />
          </button>
          <Link to="/admin/blogs" className="text-brand-blue font-bold">
            ReCircle Admin
          </Link>
          <span className="w-8" aria-hidden="true" />
        </header>

        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true">
            <button
              type="button"
              className="absolute inset-0 bg-black/40 w-full h-full"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close admin menu backdrop"
            />
            <aside className="relative flex h-full w-[min(18rem,85vw)] bg-white border-r border-gray-200 flex-col shadow-xl">
              {sidebar}
            </aside>
          </div>
        )}

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
